import React from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField,
  Typography,
} from '@mui/material';
import AdminFormRow from '../admin/AdminFormRow';
import { adminColors } from '../../styles/adminDesignTokens';
import {
  adminButtonSx,
  adminButtonToneSx,
  adminCheckboxSx,
  adminCombineSx,
  adminDialogActionsSx,
  adminDialogContentSx,
  adminDialogSx,
  adminDialogTitleSx,
  adminFormFieldSx,
  adminInlineControlLabelSx,
} from '../../styles/adminComponentStyles';

interface LottoBackfillDialogProps {
  open: boolean;
  days: string;
  loading: boolean;
  title?: string;
  description?: string;
  showForceFetch?: boolean;
  forceFetch?: boolean;
  onDaysChange: (value: string) => void;
  onForceFetchChange?: (value: boolean) => void;
  onSubmit: () => void;
  onCancel: () => void;
}

export const LottoBackfillDialog: React.FC<LottoBackfillDialogProps> = ({
  open,
  days,
  loading,
  title = 'Sync Lotto Data',
  description = 'Sync scratcher data through today for the selected store.',
  showForceFetch = false,
  forceFetch = false,
  onDaysChange,
  onForceFetchChange,
  onSubmit,
  onCancel,
}) => {
  const dayCount = Number(days);
  const isValid = Number.isInteger(dayCount) && dayCount > 0;

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: adminCombineSx(adminDialogSx, { maxWidth: '570px' }) }}
    >
      <DialogTitle
        sx={adminCombineSx(adminDialogTitleSx, {
          backgroundColor: 'rgb(239, 246, 255)',
          color: adminColors.primary,
          fontWeight: 600,
          marginBottom: 0,
        })}
      >
        {title}
      </DialogTitle>
      <DialogContent sx={adminCombineSx(adminDialogContentSx, { p: '20px', pt: '20px !important' })}>
        <Box>
          <Typography sx={{ fontSize: '14px', lineHeight: 1.3, mb: 2 }}>
            {description}
          </Typography>

          <AdminFormRow label="Days" required>
            <TextField
              autoFocus
              type="number"
              fullWidth
              value={days}
              onChange={(event) => onDaysChange(event.target.value)}
              inputProps={{
                min: '1',
                step: '1',
                inputMode: 'numeric',
                placeholder: 'Enter days',
              }}
              sx={adminFormFieldSx}
            />
          </AdminFormRow>

          <Alert severity="info" sx={{ mt: 2, borderRadius: '8px', fontSize: '13px' }}>
            This will request scratcher data starting from the entered number of days back through today.
          </Alert>

          {showForceFetch && (
            <AdminFormRow label="Force Fetch" sx={{ mt: 2 }}>
              <Box>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={forceFetch}
                      disabled={loading}
                      onChange={(event) => onForceFetchChange?.(event.target.checked)}
                      size="small"
                      sx={adminCheckboxSx}
                    />
                  }
                  label="Enabled"
                  sx={adminInlineControlLabelSx}
                />
                <Typography sx={{ color: '#808080', fontSize: '12px', fontStyle: 'italic', lineHeight: 1.3, mt: '4px' }}>
                  Download fresh Pack Status reports from the State Lottery and update the cached copies.
                </Typography>
              </Box>
            </AdminFormRow>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={adminDialogActionsSx}>
        <Button
          onClick={onCancel}
          disabled={loading}
          variant="outlined"
          sx={adminCombineSx(adminButtonSx, adminButtonToneSx.secondary, { height: 40 })}
        >
          Cancel
        </Button>
        <Button
          onClick={onSubmit}
          variant="contained"
          disabled={!isValid || loading}
          startIcon={loading ? <CircularProgress size={18} /> : null}
          sx={adminCombineSx(adminButtonSx, adminButtonToneSx.primary, { height: 40 })}
        >
          {loading ? 'Syncing...' : 'Start Sync'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
